// Thunks extracted from ChatContainerModule.js

import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { markConversationAsReadMutation } from "../ChatContainerMutation";
import { updateNavigationObject } from "modules/NavigationModule";
import { getConversationsUnreadCountQuery } from "../ChatContainerQuery";
import {
  getConversationsFromCache,
  writeConversationsFragment,
} from "../ChatContainerHelpers";
import { generateRandomId, getTaggedUserIds } from "Utils";
import moment from "moment";
import { getUserBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  createMessageMutation,
  deleteMessageMutation,
} from "modules/CommonMutations";
import { ATTACHMENT_CREATE_ATTRIBUTES } from "store/static";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  EXCLUDE_ITEM_TYPES_FOR_CONVERSATION_READ,
  STAFF_BASIC_DETAILS_PICK_KEYS,
  STUDENT_BASIC_DETAILS_PICK_KEYS,
  getChatQueryVariables,
} from "./ChatContainerUtils";

// Thunks
export const createMessage = params => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const parentId = _.get(params, "id", "");
    const label = _.get(params, "label", "");
    const showAllMessages = _.get(params, "showAllMessages", false);
    const showInstructionMessage = _.get(
      params,
      "showInstructionMessage",
      true
    );
    const extraFilters = _.get(params, "extraFilters", {});

    const collaborators = _.get(params, "collaborators", []);

    const curriculumProgramId = _.get(params, "curriculumProgramId", undefined);

    const chatQueryVariables = getChatQueryVariables({
      id: parentId,
      showAllMessages,
      showInstructionMessage,
      extraFilters,
    });
    const mainData = getConversationsFromCache(chatQueryVariables);
    const userType = getState().login.userInfo.user_type;

    const attachmentCreatedBy = _.includes(["student", "staff"], userType)
      ? _.capitalize(userType)
      : "FamilyMember";

    const attachments = _.map(params.attachments, attachment => {
      return {
        ...ATTACHMENT_CREATE_ATTRIBUTES,
        id: generateRandomId(),
        ...attachment,
        signedUrl: null,
        thumbUrl: null,
        title: null,
        streamUrl: null,
        parentType: "MESSAGE_ITEM",
        metadata: null,
        createdBy: {
          id: generateRandomId(),
          type: userType,
          __typename: attachmentCreatedBy,
        },
        __typename: "Attachment",
      };
    });

    /**
     * Make sure that newMessage contains all details that are expected in the ConversationsQuery.
     * Failing to do so, will create a situation where cache will have incomplete data after the mutation,
     * which will show loader on the UI.
     */
    const newMessage = {
      id: generateRandomId(),
      label,
      isPin: false,
      isRead: false,
      isCommentRead: true,
      itemType: "ATTACHMENTS",
      itemTypeV2: "ATTACHMENTS",
      metadata: null,
      type: "NORMAL",
      item: {
        id: generateRandomId(),
        attachments,
        __typename: "AttachmentSet",
      },
      messageReplies: {
        edges: [],
        __typename: "MessageRepliesConnection",
      },
      __typename: "Message",
      createdBy: {
        ..._.pick(
          getUserBasicDetailsFromCache({
            id: userInfo.id,
            type: userInfo.userEntityType,
          }),
          userType === "staff"
            ? STAFF_BASIC_DETAILS_PICK_KEYS
            : STUDENT_BASIC_DETAILS_PICK_KEYS
        ),
        schoolTenures: [],
        type: userType,
      },
      createdAt: moment().toISOString(),
      isDeleted: false,
    };

    const variables = {
      parentId,
      parentType: "CONVERSATION",
      label: _.trim(label),
      subEntityType: "MESSAGE",
      attachments: params.attachments,
      curriculumProgramId,
    };

    const messageType = _.get(params, "type", "");
    if (!_.isEmpty(messageType)) {
      variables.type = messageType;
      newMessage.type = messageType;
    }

    const taggedUsers = _.isEmpty(collaborators)
      ? []
      : getTaggedUserIds({
          comment: label,
          collaborators,
        });

    if (!_.isEmpty(taggedUsers)) {
      variables["taggedUsers"] = taggedUsers;
    }

    try {
      const response = await client.mutate({
        mutation: createMessageMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            createMessage: newMessage,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { createMessage },
            },
          }
        ) => {
          const ConversationMessageEdge = {
            node: createMessage,
            __typename: "ConversationMessageEdge",
          };

          const dataTosend = update(mainData, {
            messages: {
              edges: { $unshift: [ConversationMessageEdge] },
              totalCount: {
                $set: _.get(mainData, "messages.totalCount", 0) + 1,
              },
            },
          });

          setTimeout(() => {
            writeConversationsFragment({
              ...chatQueryVariables,
              data: dataTosend,
            });
          });
        },
      });

      return { ...response, successful: true };
    } catch (e) {
      setTimeout(() => {
        writeConversationsFragment({
          ...chatQueryVariables,
          data: mainData,
        });
      });

      if (e.networkError) {
        return {
          successful: false,
          errors: ["toastMsgs:no_internet_connection"],
        };
      } else {
        return {
          successful: false,
          errors: ["toastMsgs:something_went_wrong"],
        };
      }
    }
  };
};

export const getConversations = ({ conversationId, parentId, parentType }) => {
  return async (dispatch, getState) => {
    const chatInfo = getState().conversation.chatInfo;
    const shouldMarkConversationAsRead = _.get(
      chatInfo,
      "shouldMarkConversationAsRead",
      true
    );

    if (
      chatInfo.parentId == parentId.toString() &&
      parentType == chatInfo.parentType
    ) {
      dispatch(
        updateNavigationObject({
          chatBox: {
            conversationId,
          },
        })
      );
      if (shouldMarkConversationAsRead) {
        client.mutate({
          mutation: markConversationAsReadMutation,
          variables: {
            id: conversationId,
            excludeItemTypesV2: EXCLUDE_ITEM_TYPES_FOR_CONVERSATION_READ,
          },
        });
      }
    } else {
      client.query({
        query: getConversationsUnreadCountQuery,
        variables: {
          id: conversationId,
          portalType: "PLANNER",
        },
        fetchPolicy: "network-only",
      });
    }
  };
};

export const deleteMessage = ({
  id,
  conversationId,
  showAllMessages,
  showInstructionMessage,
  extraFilters,
}) => {
  return async (dispatch, getState) => {
    const chatQueryVariables = getChatQueryVariables({
      id: conversationId,
      showAllMessages,
      showInstructionMessage,
      extraFilters,
    });
    const mainData = getConversationsFromCache(chatQueryVariables);
    const includeDeletedMessage = _.get(
      extraFilters,
      "includeDeletedMessage",
      false
    );

    try {
      const response = await client.mutate({
        mutation: deleteMessageMutation,
        variables: { id },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            deleteMessage: true,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { deleteMessage },
            },
          }
        ) => {
          if (deleteMessage) {
            const indexOfElemDelete = _.findIndex(
              _.get(mainData, "messages.edges", []),
              message => {
                return message.node.id == id;
              }
            );

            const updateType = includeDeletedMessage // condition if deleted message should be shown as deleted
              ? { [indexOfElemDelete]: { node: { isDeleted: { $set: true } } } } // setting isDelete for message true
              : { $splice: [[indexOfElemDelete, 1]] }; // removing the message

            const updatedData = update(mainData, {
              messages: {
                edges: updateType,
                totalCount: {
                  $set: _.get(mainData, "messages.totalCount", 0) - 1,
                },
              },
              pinMessages: {
                edges: { $splice: [[indexOfElemDelete, 1]] },
                totalCount: {
                  $set: _.get(mainData, "pinMessages.totalCount", 0) - 1,
                },
              },
            });

            setTimeout(() => {
              writeConversationsFragment({
                ...chatQueryVariables,
                data: updatedData,
              });
            });
          }
        },
      });

      return { ...response, successful: true };
    } catch (e) {
      setTimeout(() => {
        writeConversationsFragment({
          ...chatQueryVariables,
          data: mainData,
        });
      });
      if (e.networkError) {
        return {
          successful: false,
          errors: ["toastMsgs:no_internet_connection"],
        };
      } else {
        return {
          successful: false,
          errors: ["toastMsgs:something_went_wrong"],
        };
      }
    }
  };
};

export const markConversationAsRead = ({
  id,
  excludeItemTypesV2,
  includeItemTypesV2,
  unreadMessageItemTypes,
  refetchQueries = [],
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: markConversationAsReadMutation,
        variables: {
          id,
          excludeItemTypesV2,
          includeItemTypesV2,
          unreadMessageItemTypes,
        },
        refetchQueries,
      });

      return { ...response, successful: true };
    } catch (e) {
      return {
        successful: false,
        errors: ["Something went wrong"],
      };
    }
  };
};
